import { AddIcon, ChevronDownIcon, SearchIcon, ViewIcon } from "@chakra-ui/icons";
import { Avatar, Box, Flex, HStack, IconButton, Table, Tbody, Td, Th, Thead, Tr, Text, Center, Alert, Card, Button, InputGroup, InputLeftElement, Input, useColorModeValue, AlertIcon, ModalFooter, ModalContent, Modal, ModalBody, TagLabel, Tag, ModalOverlay, ModalHeader, ModalCloseButton, Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { settingRepository, Types } from "api/setting";
import { useEffect, useRef, useState } from "react";
import TypeDrawer from "./addtype";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

const PURPLE = "#7062B9";
const PURPLE_DARK = "#5B4FA3";
const PURPLE_SOFT = "#EEEBFA";
const NAVY = "#2D2A5E";
const MUTED = "#9A98B8";

const HEADER_GRADIENT = `linear-gradient(90deg, ${PURPLE_DARK}, ${PURPLE})`;

// Solid purple primary button
const primaryBtn = {
    bg: PURPLE,
    color: "white",
    _hover: { bg: PURPLE_DARK },
    _active: { bg: PURPLE_DARK },
};

// Outlined purple button
const outlineBtn = {
    variant: "outline",
    color: PURPLE,
    borderColor: PURPLE,
    _hover: { bg: PURPLE_SOFT },
};


export default function TypesTable() {

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [types, setTypes] = useState<Types[]>([]);
    const [centerAlertVisible, setCenterAlertVisible] = useState(false);
    const [centerAlertMessage, setCenterAlertMessage] = useState("");
    const [centerAlertStatus, setCenterAlertStatus] = useState<"success" | "error" | "warning" | "info">("success");
    const cardBg = useColorModeValue("white", "#111C44");
    const mutedColor = useColorModeValue(MUTED, "whiteAlpha.600");
    const btnColor = useColorModeValue(NAVY, "white");
    const pageBg = useColorModeValue("#F0F2FF", "#0B1437");
    // Drawer state
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [selectedType, setSelectedType] = useState("");
    const [selectedValue, setSelectedValue] = useState("");
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [newValue, setNewValue] = useState("");
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewType, setViewType] = useState<Types | null>(null);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(0);
    const [hasMore, setHasMore] = useState(true);


    useEffect(() => {
        fetchTypes();
    }, [search, pageIndex, pageSize])

    const handleAddType = async () => {
        try {
            setLoading(true);

            const payload: Types = {
                id: selectedType.trim().toLowerCase(),
                type: selectedType.trim(),
                values: selectedValues,
            };

            await settingRepository.postSetting(payload);
            await fetchTypes();
            setIsFormOpen(false);

            // Reset form
            setSelectedType("");
            setSelectedValue("");
            setSelectedValues([]);
            setEditingId(null);
            setIsEditMode(false);

        } catch (error) {
            console.error("Error adding type:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateType = async () => {
        if (!editingId) {
            console.error("Editing ID is missing");
            return;
        }

        try {
            setLoading(true);

            const payload: Types = {
                id: editingId,
                type: selectedType.trim(),
                values: selectedValues,
            };

            await settingRepository.putSetting(
                editingId,
                payload
            );
            await fetchTypes();
            setIsFormOpen(false);

            setSelectedType("");
            setSelectedValue("");
            setSelectedValues([]);
            setEditingId(null);
            setIsEditMode(false);

        } catch (error) {
            console.error("Error updating type:", error);
        } finally {
            setLoading(false);
        }
    };
    const handleSaveType = async () => {
        if (!selectedType.trim()) {
            setError("Type is required");
            return;
        }

        if (selectedValues.length === 0) {
            setError("At least one value is required");
            return;
        }

        setError(null);

        if (isEditMode) {
            await handleUpdateType();
        } else {
            await handleAddType();
        }
    };


    const fetchTypes = async () => {
        try {
            setLoading(true);

            const response = await settingRepository.getSetting(
                pageIndex,
                pageSize,
                search
            );

            setTypes(response);

            setHasMore(response.length === pageSize);
        } catch (error) {
            console.error("Error fetching settings:", error);
            setTypes([]);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        const timer = window.setTimeout(() => {
            setPageIndex(0);
            setSearch(searchInput.trim());
        }, 500);

        return () => window.clearTimeout(timer);
    }, [searchInput]);


    const createPages = () => {
        const pages: number[] = [];

        for (let i = 0; i <= pageIndex; i++) {
            pages.push(i + 1);
        }

        if (hasMore) {
            pages.push(pageIndex + 2);
        }

        return pages;
    };

    return (
        <>
            {centerAlertVisible && (
                <Center position="fixed" inset={0} bg="blackAlpha.300" zIndex={9999}>
                    <Alert
                        status={centerAlertStatus}
                        variant="solid"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        width="550px"
                        borderRadius="18px"
                        py={8}
                        px={8}
                        boxShadow="2xl"
                    >
                        <AlertIcon boxSize="44px" mr={0} />
                        <Text mt={3} fontWeight="bold" fontSize="lg">
                            {centerAlertMessage}
                        </Text>
                    </Alert>
                </Center>
            )}

            <Flex mt="70px" p={2} bg={pageBg} borderRadius="25px">
                <Card bg={cardBg} borderRadius="25px" overflow="hidden" boxShadow="0 10px 30px rgba(112,98,185,0.12)" w="100%">
                    {/* ---------- Header: search and add ---------- */}
                    <Flex p={6} justify="flex-end" align="center" gap={4} wrap="wrap">
                        <HStack spacing={3}>
                            <InputGroup maxW="320px">
                                <InputLeftElement h="44px" pointerEvents="none">
                                    <SearchIcon color={PURPLE} />
                                </InputLeftElement>
                                <Input
                                    h="44px"
                                    borderRadius="14px"
                                    bg={PURPLE_SOFT}
                                    border="none"
                                    color={NAVY}
                                    _placeholder={{ color: MUTED }}
                                    _focus={{ boxShadow: `0 0 0 2px ${PURPLE}` }}
                                    placeholder="Search customer or location..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}

                                />
                            </InputGroup>

                            <Button
                                leftIcon={<AddIcon boxSize="3" />}
                                h="50px"
                                minW="max-content"
                                px={6}
                                fontSize="md"
                                whiteSpace="nowrap"
                                flexShrink={0}
                                borderRadius="14px"
                                {...primaryBtn}
                                boxShadow="0 6px 14px rgba(112,98,185,0.35)"
                                onClick={() => {
                                    setIsEditMode(false);
                                    setEditingId(null);
                                    setSelectedType("");
                                    setSelectedValue("");
                                    setNewValue("");
                                    setSelectedValues([]);

                                    setIsFormOpen(true);
                                }}
                            >
                                Add types and values
                            </Button>
                        </HStack>
                    </Flex>
                    <Flex>
                        <Box px={6} pb={2} w="100%" overflowX="auto">
                            <Table
                                variant="unstyled"
                                w="100%"
                                sx={{
                                    borderCollapse: "separate",
                                    borderSpacing: "0 10px",
                                }}
                            >
                                <Thead >
                                    <Tr justifyContent={"center"} alignItems={"center"}>
                                        <Th
                                            color="#9A98B8"
                                            fontWeight={500}
                                            fontSize="12px"
                                            textTransform="none"
                                            letterSpacing="0.02em"
                                            borderBottom="none"
                                            py={2}
                                            w="40%"
                                        >
                                            Types
                                        </Th>

                                        {/* <Th
                                            color="#9A98B8"
                                            fontWeight={500}
                                            fontSize="12px"
                                            textTransform="none"
                                            letterSpacing="0.02em"
                                            borderBottom="none"
                                            py={2}
                                            w="45%"
                                        >
                                            Values
                                        </Th> */}

                                        <Th
                                            color="#9A98B8"
                                            fontWeight={500}
                                            fontSize="12px"
                                            textTransform="none"
                                            letterSpacing="0.02em"
                                            borderBottom="none"
                                            py={2}
                                            w="15%"
                                            textAlign="center"
                                        >
                                            View Types
                                        </Th>
                                    </Tr>
                                </Thead>

                                <Tbody>
                                    {types.map((call, index) => {
                                        const rowBg =
                                            index % 2 === 0 ? "#FFFFFF" : "#FAF9FF";

                                        return (
                                            <Tr
                                                key={call.type || index}
                                                cursor="pointer"
                                                //  onClick={() => handletypeView(type)}
                                                sx={{
                                                    "& > td": {
                                                        bg: rowBg,
                                                        boxShadow:
                                                            "0 1px 3px rgba(112,98,185,0.10)",
                                                        border: "none",
                                                        color: "#2D2A5E",
                                                        transition:
                                                            "background 0.15s ease, color 0.15s ease",
                                                    },

                                                    "& > td:first-of-type": {
                                                        borderLeftRadius: "14px",
                                                    },

                                                    "& > td:last-of-type": {
                                                        borderRightRadius: "14px",
                                                    },
                                                }}
                                                _hover={{
                                                    "& > td": {
                                                        bg: "#7062B9",
                                                        color: "white",
                                                    },
                                                }}
                                            >
                                                {/* types */}
                                                <Td verticalAlign="middle" py={4}>
                                                    <HStack spacing={3}>
                                                        <Avatar
                                                            size="sm"
                                                            name={call.type}
                                                            bg="#EEEBFA"
                                                            color="#7062B9"
                                                        />

                                                        <Text
                                                            fontWeight="600"
                                                            fontSize="sm"
                                                            whiteSpace="normal"
                                                            wordBreak="break-word"
                                                        >
                                                            {call.type}
                                                        </Text>
                                                    </HStack>
                                                </Td>

                                                {/* Action */}
                                                <Td textAlign="center" py={4}>
                                                    <IconButton
                                                        className="row-action"
                                                        icon={<ViewIcon />}
                                                        aria-label="View type"
                                                        size="sm"
                                                        variant="ghost"
                                                        color="#7062B9"
                                                        _hover={{
                                                            bg: "whiteAlpha.300",
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();

                                                            setViewType(call);
                                                            setIsViewModalOpen(true);
                                                        }}
                                                    />
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    </Flex>
                    <Flex
                        w="100%"
                        justify="space-between"
                        align="center"
                        px={6}
                        pb={6}
                        pt={2}
                        flexWrap="wrap"
                        gap={3}
                    >
                        <HStack spacing={3}>

                            <Menu>
                                <Text fontSize="13px" color={mutedColor}>
                                    Rows per page
                                </Text>

                                <MenuButton
                                    as={Button}
                                    size="sm"
                                    rightIcon={<ChevronDownIcon />}
                                    bg={PURPLE_SOFT}
                                    color={PURPLE}
                                    borderRadius="10px"
                                    _hover={{ bg: "#E2DDF6" }}
                                    _active={{ bg: "#E2DDF6" }}
                                >
                                    {pageSize}
                                </MenuButton>

                                <MenuList zIndex={1000} boxShadow="lg" borderRadius="12px">
                                    {[10, 20].map((size) => (
                                        <MenuItem
                                            key={size}
                                            onClick={() => {
                                                setPageSize(size);
                                                setPageIndex(0);
                                            }}
                                        >
                                            {size}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </Menu>
                        </HStack>

                        <HStack spacing={1}>
                            <IconButton
                                aria-label="Previous page"
                                icon={<MdChevronLeft size={18} />}
                                variant="ghost"
                                size="sm"
                                color={btnColor}
                                isDisabled={pageIndex === 0}
                                onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
                            />

                            {createPages().map((num) => {
                                const active = num === pageIndex + 1;

                                return (
                                    <Button
                                        key={num}
                                        size="sm"
                                        minW="32px"
                                        h="32px"
                                        borderRadius="10px"
                                        fontWeight={active ? 700 : 500}
                                        bg={active ? PURPLE : "transparent"}
                                        color={active ? "white" : btnColor}
                                        _hover={{ bg: active ? PURPLE_DARK : PURPLE_SOFT }}
                                        onClick={() => setPageIndex(num - 1)}
                                    >
                                        {num}
                                    </Button>
                                );
                            })}

                            <IconButton
                                aria-label="Next page"
                                icon={<MdChevronRight size={18} />}
                                variant="ghost"
                                size="sm"
                                isDisabled={loading || !hasMore}
                                onClick={() => setPageIndex((prev) => prev + 1)}
                            />
                        </HStack>
                    </Flex>
                </Card>
                <TypeDrawer
                    isOpen={isFormOpen}
                    onClose={() => {
                        setIsFormOpen(false);
                    }}
                    types={types}
                    isEditMode={isEditMode}
                    editingId={editingId}
                    selectedType={selectedType}
                    setSelectedType={setSelectedType}
                    selectedValue={selectedValue}
                    setSelectedValue={setSelectedValue}
                    selectedValues={selectedValues}
                    setSelectedValues={setSelectedValues}
                    onSave={handleSaveType}
                />
                <Modal
                    isOpen={isViewModalOpen}
                    onClose={() => {
                        setIsViewModalOpen(false);
                        setViewType(null);
                    }}
                    isCentered
                    size="md"
                >
                    <ModalOverlay />

                    <ModalContent
                        borderRadius="20px"
                        overflow="hidden"
                    >
                        {/* Header */}

                        <ModalHeader
                            color="#2D2A5E"
                            fontSize="22px"
                            fontWeight="600"
                            pb={3}
                        >
                            Type Details
                        </ModalHeader>

                        <ModalCloseButton />

                        <ModalBody>
                            {/* TYPE */}

                            <Box mb={6}>
                                <Text
                                    fontSize="13px"
                                    color="gray.500"
                                    mb={2}
                                >
                                    Type
                                </Text>

                                <Box
                                    bg={PURPLE_SOFT}
                                    borderRadius="12px"
                                    px={4}
                                    py={3}
                                >
                                    <Text
                                        fontSize="16px"
                                        fontWeight="600"
                                        color={PURPLE}
                                    >
                                        {viewType?.type || "-"}
                                    </Text>
                                </Box>
                            </Box>

                            {/* VALUES */}

                            <Box>
                                <Text
                                    fontSize="13px"
                                    color="gray.500"
                                    mb={3}
                                >
                                    Values
                                </Text>

                                {viewType?.values &&
                                    viewType.values.length > 0 ? (
                                    <HStack
                                        spacing={2}
                                        wrap="wrap"
                                    >
                                        {viewType.values.map(
                                            (value) => (
                                                <Tag
                                                    key={value}
                                                    size="md"
                                                    borderRadius="full"
                                                    bg={PURPLE_SOFT}
                                                    color={PURPLE}
                                                >
                                                    <TagLabel>
                                                        {value}
                                                    </TagLabel>
                                                </Tag>
                                            )
                                        )}
                                    </HStack>
                                ) : (
                                    <Text
                                        color="gray.500"
                                        fontSize="14px"
                                    >
                                        No values available.
                                    </Text>
                                )}
                            </Box>
                        </ModalBody>

                        {/* FOOTER */}

                        <ModalFooter gap={3}>
                            <Button
                                variant="outline"
                                borderColor={PURPLE}
                                color={PURPLE}
                                borderRadius="12px"
                                onClick={() => {
                                    setIsViewModalOpen(false);
                                    setViewType(null);
                                }}
                            >
                                Close
                            </Button>

                            <Button
                                bg={PURPLE}
                                color="white"
                                borderRadius="12px"
                                _hover={{
                                    bg: PURPLE_DARK,
                                }}
                                onClick={() => {
                                    if (!viewType) return;

                                    // Close View Modal
                                    setIsViewModalOpen(false);

                                    // Enable Edit Mode
                                    setIsEditMode(true);

                                    // Pass selected record to Drawer
                                    setEditingId(viewType.id);

                                    setSelectedType(viewType.type);

                                    setSelectedValues(
                                        viewType.values || []
                                    );

                                    setSelectedValue("");

                                    // Open Add/Edit Drawer
                                    setIsFormOpen(true);
                                }}
                            >
                                Edit
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </Flex>
        </>
    )

}
